import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const Attendance = () => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({});

  // Filters
  const [eventFilter, setEventFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => { fetchAttendance(1); }, [eventFilter, typeFilter, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchAttendance = async (pageNumber = 1) => {
    try {
      setLoading(true);
      const params = {
        page: pageNumber,
        event_id: eventFilter !== 'all' ? eventFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        search: debouncedSearch || undefined,
      };
      const response = await api.get('/organizer/attendance', { params });

      const paginator = response.data.data.attendance;
      setRecords(paginator.data || []);
      setPagination(paginator);
      setStats(response.data.data.stats || {});
    } catch (error) {
      toast.error('Failed to load attendance');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/organizer/events');
      setEvents(response.data.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const renderPagination = () => {
    if (!pagination.last_page || pagination.last_page <= 1) return null;

    const current = pagination.current_page;
    const last = pagination.last_page;
    const maxPages = 3;

    let start = Math.max(1, current - Math.floor(maxPages / 2));
    let end = start + maxPages - 1;

    if (end > last) {
      end = last;
      start = Math.max(1, end - maxPages + 1);
    }

    const pageNumbers = [];
    for (let i = start; i <= end; i++) pageNumbers.push(i);

    return (
      <nav className="mt-4">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
            <button className="page-link text-gray-900" onClick={() => fetchAttendance(current - 1)}>
              Previous
            </button>
          </li>

          {start > 1 && (
            <>
              <li className="page-item">
                <button className="page-link text-gray-900" onClick={() => fetchAttendance(1)}>1</button>
              </li>
              {start > 2 && (
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              )}
            </>
          )}

          {pageNumbers.map(num => (
            <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
              <button
                className={`page-link text-gray-900 ${
                  num === current ? '!bg-purple-500 !text-white !border-purple-500 border' : ''
                }`}
                onClick={() => fetchAttendance(num)}
              >
                {num}
              </button>
            </li>
          ))}

          {end < last && (
            <>
              {end < last - 1 && (
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              )}
              <li className="page-item">
                <button className="page-link text-gray-900" onClick={() => fetchAttendance(last)}>{last}</button>
              </li>
            </>
          )}

          <li className={`page-item ${current === last ? 'disabled' : ''}`}>
            <button className="page-link text-gray-900" onClick={() => fetchAttendance(current + 1)}>
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleResetFilters = () => {
    setSearch('');
    setEventFilter('all');
    setTypeFilter('all');
  };

  return (
    <div>
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h5 className="h5 mb-0 text-gray-900 font-weight-bold">
          Attendance
        </h5>
      </div>

      {/* Stats Row */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Total Scans</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total_scans || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-qrcode fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Check-ins</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.checkins || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-sign-in-alt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Check-outs</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.checkouts || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-sign-out-alt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Currently Inside</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.currently_inside || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-user-check fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Attendee or scanner name / email ..."
              className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
            />
          </div>

          {/* Event Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Event</label>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
            >
              <option value="all">All Events</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
            >
              <option value="all">All Types</option>
              <option value="checkin">Check In</option>
              <option value="checkout">Check Out</option>
            </select>
          </div>

          {/* Reset Button */}
          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="btn btn-outline-danger w-20"
              disabled={!search && eventFilter === 'all' && typeFilter === 'all'}
            >
              <i className="fas fa-redo mr-2"></i>Reset
            </button>
          </div>
        </div>
      </div>

      {/* Attendance Table Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="m-0 font-weight-bold text-gray-900 font-weight-bold">All Scans</h6>
            </div>
          </div>
        </div>
        <div className="card-body p-4">
          {loading ? (
            <div className="text-center py-12">
              <svg
                className="animate-spin h-12 w-12 mx-auto text-purple-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <p className="mt-4 text-gray-600">Loading attendance...</p>
            </div>
          ) :
            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table className="table table-hover mb-0">
                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ fontSize: '0.8rem' }}>Attendee</th>
                    <th style={{ fontSize: '0.8rem' }}>Event</th>
                    <th style={{ fontSize: '0.8rem' }}>Type</th>
                    <th style={{ fontSize: '0.8rem' }}>Scanned By</th>
                    <th style={{ fontSize: '0.8rem' }}>Ticket</th>
                    <th style={{ fontSize: '0.8rem' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length > 0 ? (
                    records.map((rec) => (
                      <tr key={rec.id} style={{ fontSize: '0.85rem' }}>
                        <td>
                          <div>
                            <div className="font-weight-bold">{rec.attendee_name}</div>
                            <small className="text-muted">{rec.attendee_email}</small>
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '180px' }}>
                            {rec.event_title}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${rec.transaction_type === 'checkin' ? 'badge-success' : 'badge-warning'}`}>
                            {rec.transaction_type === 'checkin' ? 'Check In' : 'Check Out'}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="font-weight-bold">{rec.agent_name || '—'}</div>
                            {rec.agent_email && <small className="text-muted">{rec.agent_email}</small>}
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">{rec.ticket_qr_code}</small>
                        </td>
                        <td className="text-nowrap">{formatDate(rec.transaction_datetime)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        {debouncedSearch ? 'No scans found matching your search' : 'No attendance records yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          }
        </div>
        {renderPagination()}
      </div>
    </div>
  );
};

export default Attendance;
